"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import apiClient from "@/libs/api";
import config from "@/config";
import { storeSessionForRedirect, storePurchaseContext, getPurchaseContext } from "@/libs/ga";
import { trackFunnel } from "@/libs/analytics";
import AuthModal from "./AuthModal";

// Unified component for both subscription plans and credit packages
// Handles Stripe Checkout Sessions for payments and subscriptions
const ButtonCheckout = ({
  priceId,
  mode = "payment",
  text,
  className,
  style,
  quantity,
  couponId,
  credits,
  showIcon = false,
  purchaseTrigger = "voluntary",
}: {
  priceId: string;
  mode?: "payment" | "subscription";
  text?: string;
  className?: string;
  style?: React.CSSProperties;
  quantity?: number;
  couponId?: string;
  credits?: number;
  showIcon?: boolean;
  purchaseTrigger?: "voluntary" | "forced";
}) => {
  const { data: session } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);

  const handlePayment = async () => {
    // Check if user is authenticated
    if (!session?.user) {
      // Guest user - show auth modal instead of proceeding to Stripe

      // Get existing context and merge
      const existingContext = getPurchaseContext(false) || {};

      // Store purchase context for after auth
      const purchaseContext = {
        ...existingContext,
        price_id: priceId,
        mode: mode,
        credits: credits,
        quantity: mode === "subscription" ? 1 : quantity,
        has_coupon: !!couponId,
        purchase_trigger: existingContext.purchase_trigger || purchaseTrigger,
        package_type: mode === "subscription" ? "subscription" : "topup"
      };
      storePurchaseContext(purchaseContext);

      // Track auth modal shown
      trackFunnel('monetization', 'auth_required_for_purchase', {
        ...purchaseContext,
        trigger_point: 'checkout_button'
      });

      // Show auth modal
      setShowAuthModal(true);
      return;
    }

    // User is authenticated - proceed with checkout
    setIsLoading(true);

    // Store session data before Stripe redirect
    storeSessionForRedirect('stripe');

    // Get existing context (e.g., from insufficient credits modal) and merge with new data
    const existingContext = getPurchaseContext(false) || {};

    // Compute estimated value for GA4 ecommerce tracking
    let estimatedValue = 0;
    if (mode === "subscription") {
      const plan = config.stripe.plans.find(p => p.priceId === priceId || p.yearlyPriceId === priceId);
      if (plan) estimatedValue = plan.price;
    } else {
      const qty = quantity || 0;
      const basePrice = qty * config.stripe.creditTopups.baseRate;
      const pkg = config.stripe.creditTopups.packages.find(p => p.credits === qty);
      estimatedValue = pkg ? Math.round(basePrice * (1 - pkg.discount / 100) * 100) / 100 : basePrice;
    }

    // Build new purchase context, preserving existing trigger and metadata
    const purchaseContext = {
      ...existingContext, // Keep forced trigger, tool_name, credits_required, etc.
      price_id: priceId,
      mode: mode,
      credits: credits,
      quantity: mode === "subscription" ? 1 : quantity, // Subscriptions always 1, payments require valid quantity
      has_coupon: !!couponId,
      // Only override purchase_trigger if it's not already set (voluntary is default)
      purchase_trigger: existingContext.purchase_trigger || purchaseTrigger,
      package_type: mode === "subscription" ? "subscription" : "topup",
      value: estimatedValue,
    };
    storePurchaseContext(purchaseContext);

    // Track checkout initiation
    trackFunnel('monetization', 'checkout_started', purchaseContext);

    try {
      const { url }: { url: string } = await apiClient.post(
        "/stripe/create-checkout",
        {
          priceId,
          successUrl: `${window.location.origin}${config.payments.successUrl}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}${config.payments.cancelUrl}`,
          mode,
          quantity: mode === "subscription" ? 1 : quantity, // Subscriptions always 1, payments require valid quantity (validated server-side)
          couponId: couponId || null,
        }
      );

      // Track successful checkout creation with full context
      trackFunnel('monetization', 'checkout_created', purchaseContext);

      window.location.href = url;
    } catch (e) {
      console.error(e);

      // Track checkout error with full context
      trackFunnel('monetization', 'checkout_error', {
        ...purchaseContext,
        error: e.message || 'Unknown error'
      });
    }

    setIsLoading(false);
  };


  // Generate default text based on context
  const getButtonText = () => {
    if (text) return text;
    if (credits) return `Buy ${credits} Credits`;
    return `Get ${config?.appName}`;
  };

  return (
    <>
      <button
        className={className || "btn btn-primary btn-block group text-xl"}
        style={style}
        onClick={() => handlePayment()}
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="loading loading-spinner loading-xs"></span>
        ) : (
          <>
            {showIcon && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h-6.75"
                />
              </svg>
            )}
            {getButtonText()}
          </>
        )}
      </button>

      {/* Auth Modal for guest users */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {}} // Not used - page refreshes after auth
        callbackUrl="/dashboard/pricing"
        purchaseContext={{
          priceId,
          credits,
          mode,
        }}
      />
    </>
  );
};

export default ButtonCheckout;
